import contentful from 'clients/contentful'
import BlogPosts from 'components/blog'
import BlogMeta from 'components/blog/meta'
import BlogPost from 'components/blog/post'
import Layout from 'components/layout'
import * as R from 'ramda'
import { createMetaTags, createSlug } from 'utilities/blog'
import { UrbanistProvider } from 'providers/font-providers/urbanist-provider'
import { useIsMobileLayout } from '../../components/dashboard/clients/hooks'
import { AnalyticsPage } from 'containers/analytics/page'
import { useRouter } from 'next/router'
import LoadingScreen from 'components/shared/loading-screen'

export default function BlogPage({
  title,
  metadata,
  topNav,
  template,
  footer,
  categories,
  experts,
  post,
  isHomepage,
  postsByCategory,
  editorialIntegrity,
  relatedPosts,
  latestAuthorPosts,
  latestPosts,
  featuredPosts,
  otherCategories,
}) {
  const isMobile = useIsMobileLayout()
  const router = useRouter()

  return (
    <UrbanistProvider>
      <Layout title={`${title || 'Blog'} | Kickoff`}>
        <AnalyticsPage name="Blog" trackView={!post}>
          {router.isFallback && <LoadingScreen />}
          {metadata && <BlogMeta metadata={metadata} />}
          {post && (
            <AnalyticsPage
              name="Blog Page"
              properties={{ slug: post?.fields?.slug }}
              trackView={false}>
              <BlogPost
                topNav={topNav}
                post={post}
                relatedPosts={relatedPosts}
                footer={footer}
                latestAuthorPosts={latestAuthorPosts}
                latestPosts={latestPosts}
                isMobile={isMobile}
              />
            </AnalyticsPage>
          )}
          {featuredPosts && (
            <BlogPosts
              title={title || 'Featured'}
              topNav={topNav}
              template={template}
              categories={categories}
              experts={experts}
              latestPosts={latestPosts}
              featuredPosts={featuredPosts}
              otherCategories={otherCategories}
              postsByCategory={postsByCategory}
              editorialIntegrity={editorialIntegrity}
              footer={footer}
              isHomepage={isHomepage}
            />
          )}
        </AnalyticsPage>
      </Layout>
    </UrbanistProvider>
  )
}

export async function getStaticProps() {
  const [
    topNavRes,
    templateRes,
    footerRes,
    catRes,
    expertRes,
    blogPostRes,
    eiRes,
  ] = await Promise.all([
    contentful.getEntries({
      content_type: 'blogTopNavigationBar',
      include: 3,
    }),
    contentful.getEntries({
      content_type: 'blogPageTemplate',
      'fields.name': 'Main Page',
      include: 3,
    }),
    contentful.getEntries({
      content_type: 'blogFooter',
      include: 3,
    }),
    contentful.getEntries({
      content_type: 'category',
      'fields.featured': true,
      include: 10,
      order: '-sys.createdAt',
      limit: 6,
    }),
    contentful.getEntries({
      content_type: 'author',
      'fields.type': 'expert',
      include: 10,
    }),
    contentful.getEntries({
      content_type: 'newBlogPost',
      'fields.slug[nin]': 'editorial-guidelines',
      order: '-sys.createdAt',
    }),
    contentful.getEntries({
      content_type: 'editorialIntegrity',
      include: 10,
    }),
  ])

  const templateIndex = templateRes.items.length - 1

  const posts = blogPostRes.items
  const latestPosts = R.take(4, posts)

  const templateFeaturedPosts = R.take(
    4,
    templateRes.items[templateIndex].fields?.featuredPosts || []
  )
  let remainingPosts = R.uniq([...templateFeaturedPosts, ...R.drop(4, posts)])

  const featuredPosts = R.take(4, remainingPosts)
  remainingPosts = R.drop(4, remainingPosts)

  const postsByCategory = R.reduce(
    (acc, { fields, sys }) => {
      R.map(
        ({ fields: f }) => {
          if (!acc[f.name]) {
            acc[f.name] = { name: f.name, slug: createSlug(f), posts: [] }
          }
          if (acc[f.name].posts.length < 3) {
            acc[f.name].posts.push({ fields, sys })
          }
        },
        R.filter(({ fields: f }) => !f.parent, fields.categories || [])
      )
      return acc
    },
    {},
    remainingPosts
  )

  return {
    props: {
      topNav: topNavRes.items[0],
      template: templateRes.items[templateIndex],
      footer: footerRes.items[0],
      categories: catRes.items,
      experts: expertRes.items,
      isHomepage: true,
      editorialIntegrity: eiRes?.items[0] || [],
      latestPosts,
      featuredPosts,
      postsByCategory,
      metadata: createMetaTags({
        ...templateRes.items[templateIndex],
        fields: {
          ...templateRes.items[templateIndex].fields,
          type: 'website',
        },
      }),
    },
    revalidate: 10,
  }
}
